import React, { useState, useEffect } from 'react'
import {
  Tabs,
  TabList,
  Item,
  TabPanels,
  Heading,
  Text,
  View,
  Flex,
  Well,
  StatusLight,
  Provider,
  Button
} from '@adobe/react-spectrum'
import { customSpectrumTheme, SPECTRUM_THEME_CLASS } from '../../themes/spectrum-theme'
import {
  BookOpen,
  GraduationCap,
  HelpCircle,
  Layers,
  Search,
  ShoppingBag,
  RefreshCw,
  TabletSmartphone, ListChecks, TrendingUp,
  BarChart3,
  FileText,
  X,
  ChevronRight
} from 'lucide-react'
import { Link } from 'react-router-dom'
import RecentJobsTable from './RecentJobsTable'
import DevicesTable from './DevicesTable'
import type { ActiveFeatureModel } from '../../types/generated/models/active-feature-model'
import './HomeImproved.css'

interface HomeImprovedProps {
  translate: (key: string) => string
  jobs?: any
  devices?: any
  jobsPagination?: any
  devicesPagination?: any
  jobsPage?: number
  devicesPage?: number
  jobsSearch?: string
  devicesSearch?: string
  user?: any
  overview?: any
  isLoading?: boolean
  onRefresh?: () => void
  onJobsPageChange?: (pageNumber: number) => void
  onDevicesPageChange?: (pageNumber: number) => void
  onJobsSearchChange?: (value: string) => void
  onDevicesSearchChange?: (value: string) => void
  activeFeatures?: ActiveFeatureModel[] | null
  servicesUrl?: string
  shopUrl?: string
  helpUrl?: string
  impersonating?: boolean
  softwareUpdate?: boolean
}

const HomeImproved: React.FC<HomeImprovedProps> = ({
  translate,
  jobs,
  devices,
  jobsPagination,
  devicesPagination,
  jobsPage = 1,
  devicesPage = 1,
  jobsSearch = '',
  devicesSearch = '',
  user,
  overview,
  isLoading,
  onRefresh,
  onJobsPageChange,
  onDevicesPageChange,
  onJobsSearchChange,
  onDevicesSearchChange,
  activeFeatures,
  helpUrl,
  impersonating,
  softwareUpdate
}) => {
  const [selectedTab, setSelectedTab] = useState<string>('jobs')
  const [refreshing, setRefreshing] = useState(isLoading || false)
  const [devicesLoaded, setDevicesLoaded] = useState(false)

  const hasFeature = (featureName: string) =>
    activeFeatures?.some((f) => f.featureName === featureName) ?? false

  const label = (key: string, fallback: string) => {
    const translated = translate(key)
    return translated === key ? fallback : translated
  }

  const jobsData = jobs?.data || []
  const devicesData = devices?.data || []

  const handleRefresh = () => {
    setRefreshing(true)
    if (onRefresh) {
      onRefresh()
    }
    setTimeout(() => {
      setRefreshing(false)
    }, 1000)
  }

  const handleTabChange = (key: string) => {
    setSelectedTab(key)
    if (key === 'devices' && !devicesLoaded) {
      setDevicesLoaded(true)
    }
  }

  // KPI data with dual metrics
  const kpis = [
    {
      label: label('registered_devices', 'Registered Devices'),
      value: String(overview?.registeredDevices || '0'),
      icon: TabletSmartphone,
      detailValues: undefined
    },
    {
      label: label('general_jobs', 'Jobs'),
      value: String(overview?.jobsCompleted || '0'),
      icon: ListChecks,
      detailValues: undefined
    },
    {
      label: label('performed_total_tests', 'Performed/Total Tests'),
      value: `${overview?.performedTests || 0}/${overview?.testsCompleted || 0}`,
      icon: BarChart3,
      detailValues: undefined
    },
    {
      label: label('success_test_rate', 'Success Test Rate'),
      value: `${overview?.successRate || 0}%`,
      icon: TrendingUp,
      detailValues: undefined
    }
  ]

  // Quick action cards
  const allQuickActions = [
    {
      id: 'all-jobs',
      label: translate('all_jobs'),
      description: translate('view_manage_all_jobs'),
      icon: Layers,
      link: '/jobs',
      feature: null
    },
    {
      id: 'shop',
      label: translate('shop'),
      description: translate('browse_purchase'),
      icon: ShoppingBag,
      link: 'https://www.trend-networks.com/product-category/copper-data-cable-tester/',
      feature: null,
      external: true
    },
    {
      id: 'help',
      label: translate('help'),
      description: translate('get_support_docs'),
      icon: HelpCircle,
      link: helpUrl || 'https://www.trend-networks.com/support/downloads/',
      feature: null,
      external: true
    },
    {
      id: 'product-manuals',
      label: translate('product_manuals'),
      description: translate('download_product_manuals'),
      icon: BookOpen,
      link: 'https://www.trend-networks.com/support/downloads/manuals/',
      feature: null,
      external: true
    },
    {
      id: 'metrics',
      label: translate('organisation_metrics_label'),
      description: translate('view_metrics_analytics'),
      icon: BarChart3,
      link: '/metrics',
      feature: 'organisation_metrics'
    },
    {
      id: 'reports',
      label: translate('reports'),
      description: translate('view_download_reports'),
      icon: FileText,
      link: '/reportlist',
      feature: null
    },
    {
      id: 'trend-academy',
      label: translate('trend_academy'),
      description: translate('trend_academy_description'),
      icon: GraduationCap,
      link: 'https://trendnetworksacademy.talentlms.com/plus/',
      feature: null,
      external: true
    }
  ]

  const quickActions = allQuickActions.filter(
    (action) => action.feature === null || hasFeature(action.feature)
  )

  const renderTableSearch = () => {
    const isDevices = selectedTab === 'devices'
    const value = isDevices ? devicesSearch : jobsSearch
    const onChange = isDevices ? onDevicesSearchChange : onJobsSearchChange

    return (
      <div className="home-table-search">
        <Search size={14} aria-hidden="true" className="home-table-search-icon" />
        <input
          aria-label={translate('search')}
          placeholder={translate('search')}
          value={value || ''}
          onChange={(event) => onChange && onChange(event.target.value)}
          className="home-table-search-input"
        />
        {value ? (
          <button
            type="button"
            aria-label={translate('general_close')}
            onClick={() => onChange && onChange('')}
            className="home-table-search-clear"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
    )
  }

  const renderPagination = (pagination: any, currentPage: number, onPageChange?: (pageNumber: number) => void) => {
    const totalPages = Math.max(pagination?.totalPages || 1, 1)
    const safeCurrentPage = Math.min(Math.max(currentPage || 1, 1), totalPages)

    return (
      <Flex gap="size-200" alignItems="center" justifyContent="center" marginTop="size-200">
        <Button
          variant="secondary"
          isDisabled={safeCurrentPage === 1}
          onPress={() => onPageChange && onPageChange(safeCurrentPage - 1)}
        >
          {translate('general_table_previous')}
        </Button>
        <Text>
          {translate('general_table_page')} {safeCurrentPage} {translate('general_table_of')} {totalPages}
        </Text>
        <Button
          variant="secondary"
          isDisabled={safeCurrentPage === totalPages}
          onPress={() => onPageChange && onPageChange(safeCurrentPage + 1)}
        >
          {translate('general_table_next')}
        </Button>
      </Flex>
    )
  }

  return (
    <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme={'light'}>
      <View
        UNSAFE_className="home-improved"
        UNSAFE_style={{ paddingTop: impersonating && softwareUpdate ? '200px' : impersonating || softwareUpdate ? '140px' : '80px' }}
      >
        <Flex direction="column" gap="size-300">
          {/* Header */}
          <View UNSAFE_className="home-page-header">
            <Flex justifyContent="space-between" alignItems="center" gap="size-200">
              <Heading marginBottom={0} level={1} UNSAFE_className="home-welcome-heading">{translate('welcome_back')}, {user?.name || translate('default_user_name')}</Heading>
              <Button
                variant="secondary"
                onPress={handleRefresh}
                isDisabled={refreshing}
                UNSAFE_className="home-refresh-button"
              >
                <RefreshCw size={18} style={{ marginRight: '8px', color: '#f15a24' }} />
                {refreshing ? translate('refreshing') : translate('refresh')}
              </Button>
            </Flex>
          </View>

          {/* KPI Cards */}
          <div className="home-kpi-grid">
            {kpis.map((kpi) => {
              const Icon = kpi.icon
              return (
                <div key={kpi.label} className={`home-kpi-card ${kpi.icon === TrendingUp ? 'home-kpi-card-success' : ''}`}>
                  <div className="home-kpi-icon">
                    <Icon size={27} aria-hidden="true" />
                  </div>
                  <div className="home-kpi-content">
                    <Text UNSAFE_className="home-kpi-label">
                      {kpi.label}
                    </Text>
                    {kpi.detailValues ? (
                      <div className="home-kpi-detail-values">
                        {kpi.detailValues.map((detail) => (
                          <div
                            key={detail.label}
                            className="home-kpi-detail"
                          >
                            <Text UNSAFE_className="home-kpi-detail-label">
                              {detail.label}
                            </Text>
                            <Text UNSAFE_className="home-kpi-detail-value">
                              {detail.value}
                            </Text>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Heading level={2} marginBottom={0} marginTop={0} UNSAFE_className="home-kpi-value">
                        {kpi.value}
                      </Heading>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Quick Actions Section */}
          <View marginTop="size-100" UNSAFE_className="home-quick-actions-section">
            <Heading marginTop={0} level={2} marginBottom="size-300" UNSAFE_className="home-section-heading">{translate('quick_actions')}</Heading>
            <div className="home-quick-actions-grid">
              {quickActions.map((action) => {
                const Icon = action.icon
                const cardContent = (
                    <div className="home-quick-action-tile">
                      <div className="home-quick-action-icon"><Icon size={27} aria-hidden="true" /></div>
                      <div className="home-quick-action-copy">
                        <Heading level={4} marginTop={0} marginBottom={0} UNSAFE_className="home-quick-action-title">{action.label}</Heading>
                        <Text UNSAFE_className="home-quick-action-description">{action.description}</Text>
                      </div>
                      <ChevronRight size={23} aria-hidden="true" className="home-quick-action-chevron" />
                    </div>
                )
                return (action as any).external ? (
                  <a key={action.id} href={action.link} target="_blank" rel="noopener noreferrer" className="home-quick-action-link">
                    {cardContent}
                  </a>
                ) : (
                  <Link key={action.id} to={action.link} className="home-quick-action-link">
                    {cardContent}
                  </Link>
                )
              })}
            </div>
          </View>

          {/* Tabs for Jobs and Devices */}
          <View UNSAFE_className="home-data-section" marginTop="24px">
            {renderTableSearch()}
            <Tabs
              selectedKey={selectedTab}
              onSelectionChange={(key) => handleTabChange(key as string)}
            >
              <TabList>
                <Item key="jobs">{translate('recent_jobs')}</Item>
                <Item key="devices">{translate('devices')}</Item>
              </TabList>
              <TabPanels>
                <Item key="jobs">
                  {jobsData && jobsData.length > 0 ? (
                    <>
                      <RecentJobsTable jobs={jobsData} translate={translate} />
                      {renderPagination(jobsPagination, jobsPage, onJobsPageChange)}
                    </>
                  ) : (
                    <Text>{translate('no_recent_jobs_found')}</Text>
                  )}
                </Item>
                <Item key="devices">
                  {devicesLoaded && devicesData && devicesData.length > 0 ? (
                    <>
                      <DevicesTable devices={devicesData} translate={translate} />
                      {renderPagination(devicesPagination, devicesPage, onDevicesPageChange)}
                    </>
                  ) : devicesLoaded && (!devicesData || devicesData.length === 0) ? (
                    <Text>{translate('no_devices_found')}</Text>
                  ) : (
                    <Text>{translate('loading_devices')}</Text>
                  )}
                </Item>
              </TabPanels>
            </Tabs>
          </View>
        </Flex>
      </View>
    </Provider>
  )
}

export default HomeImproved
